import { useEffect, useId, useImperativeHandle, useLayoutEffect, useRef, useState, type ReactNode, type Ref, type TextareaHTMLAttributes } from "react";
import { createPortal } from "react-dom";
import "./mentionTextarea.css";

export type MentionTarget = { start: number; end: number; query: string };
export type MentionReplacement = { text: string; caret: number; selectionEnd: number };
export type MentionTextareaProps<T> = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> & {
  ref?: Ref<HTMLTextAreaElement>;
  value: string;
  contextKey: string;
  active?: boolean;
  selectedNames: readonly string[];
  hint: string;
  resourceLabel: string;
  popupTitle: string;
  listLabel: string;
  search: (query: string) => Promise<T[]>;
  getMention: (input: HTMLTextAreaElement, selectedNames: readonly string[]) => MentionTarget | null;
  getKey: (candidate: T) => string;
  getName: (candidate: T) => string;
  getLabel: (candidate: T) => string;
  renderCandidate?: (candidate: T) => { content: ReactNode; description?: ReactNode } | undefined;
  replaceMention: (text: string, mention: MentionTarget, candidate: T) => MentionReplacement;
  onValueChange: (text: string, candidate?: T) => void;
};

/** 复制输入框排版定位光标；不读取或推断候选的业务含义。 */
function caretBounds(input: HTMLTextAreaElement) {
  const bounds = input.getBoundingClientRect(), style = getComputedStyle(input);
  const mirror = document.createElement("div");
  for (const property of ["box-sizing", "font-family", "font-size", "font-weight", "font-style", "line-height", "letter-spacing", "text-indent", "padding", "border", "tab-size", "direction"]) mirror.style.setProperty(property, style.getPropertyValue(property));
  Object.assign(mirror.style, { position: "fixed", visibility: "hidden", pointerEvents: "none", left: `${bounds.left}px`, top: `${bounds.top}px`, width: `${bounds.width}px`, whiteSpace: "pre-wrap", overflowWrap: "break-word" });
  mirror.textContent = input.value.slice(0, input.selectionStart);
  const marker = document.createElement("span");
  marker.textContent = "\u200b"; mirror.append(marker); document.body.append(mirror);
  const caret = marker.getBoundingClientRect(), lineHeight = Number.parseFloat(style.lineHeight) || 26;
  mirror.remove();
  return { left: Math.max(bounds.left, Math.min(caret.left - input.scrollLeft, bounds.right)), top: Math.max(bounds.top, Math.min(caret.top - input.scrollTop, bounds.bottom - lineHeight)), lineHeight, width: bounds.width };
}

/** 仅承载光标、异步候选、键盘和浮层；候选资格与文本替换由业务输入组件负责。 */
export function MentionTextarea<T>({ ref, value, contextKey, active = true, selectedNames, hint, resourceLabel, popupTitle, listLabel, search, getMention, getKey, getName, getLabel, renderCandidate, replaceMention, onValueChange, disabled, onSelect, onClick, onKeyUp, onKeyDown, onBlur, onCompositionStart, onCompositionEnd, ...props }: MentionTextareaProps<T>) {
  const inputRef = useRef<HTMLTextAreaElement>(null), panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef(search); searchRef.current = search;
  const composing = useRef(false), dismissed = useRef("");
  const lastChoice = useRef({ context: "", name: "" });
  const id = useId();
  const [mention, setMention] = useState<(MentionTarget & { context: string; text: string }) | null>(null);
  const [result, setResult] = useState<{ key: string; items: T[]; error: boolean }>({ key: "", items: [], error: false });
  const [selected, setSelected] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 280, maxHeight: 260 });
  useImperativeHandle(ref, () => inputRef.current!, []);
  const current = active && !disabled && mention?.context === contextKey && mention.text === value ? mention : null;
  const queryKey = current ? JSON.stringify([contextKey, current.start, current.end, current.query]) : "";
  const ready = Boolean(queryKey && result.key === queryKey), items = ready ? result.items : [];
  const signature = (input: HTMLTextAreaElement) => `${input.value}:${input.selectionStart}:${input.selectionEnd}`;
  const syncMention = (input: HTMLTextAreaElement) => {
    if (composing.current || dismissed.current === signature(input)) return;
    const names = [...selectedNames];
    if (lastChoice.current.context === contextKey) names.push(lastChoice.current.name);
    const target = getMention(input, names);
    setMention(target ? { ...target, context: contextKey, text: input.value } : null);
  };
  const close = () => { if (inputRef.current) dismissed.current = signature(inputRef.current); setMention(null); };

  useEffect(() => {
    if (!queryKey || !current) return;
    let stale = false;
    setSelected(0);
    const timer = window.setTimeout(() => {
      Promise.resolve().then(() => searchRef.current(current.query)).then(candidates => { if (!stale) setResult({ key: queryKey, items: candidates, error: false }); }).catch(() => { if (!stale) setResult({ key: queryKey, items: [], error: true }); });
    }, 500);
    return () => { stale = true; window.clearTimeout(timer); };
  }, [queryKey]);

  useLayoutEffect(() => {
    if (!current) return;
    const place = () => {
      if (!inputRef.current) return;
      const caret = caretBounds(inputRef.current), viewport = window.visualViewport;
      const width = Math.min(Math.max(caret.width, 280), (viewport?.width || window.innerWidth) - 24);
      const leftEdge = (viewport?.offsetLeft || 0) + 12, topEdge = (viewport?.offsetTop || 0) + 12;
      const bottomEdge = (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight) - 12;
      const below = bottomEdge - caret.top - caret.lineHeight - 6, above = caret.top - topEdge - 6;
      const upwards = below < 220 && above > below, maxHeight = Math.max(40, Math.min(260, upwards ? above : below));
      setPosition({ left: Math.max(leftEdge, Math.min(caret.left, leftEdge + (viewport?.width || window.innerWidth) - width - 24)), top: Math.max(topEdge, upwards ? caret.top - maxHeight - 6 : caret.top + caret.lineHeight + 6), width, maxHeight });
    };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape" && !event.isComposing && !composing.current) { event.preventDefault(); event.stopPropagation(); close(); } };
    place(); window.addEventListener("resize", place); window.addEventListener("scroll", place, true); window.addEventListener("keydown", escape, true);
    window.visualViewport?.addEventListener("resize", place); window.visualViewport?.addEventListener("scroll", place);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); window.removeEventListener("keydown", escape, true); window.visualViewport?.removeEventListener("resize", place); window.visualViewport?.removeEventListener("scroll", place); };
  }, [current]);
  useEffect(() => { panelRef.current?.querySelector<HTMLElement>(`[id="${id}-option-${selected}"]`)?.scrollIntoView?.({ block: "nearest" }); }, [selected, id]);

  const choose = (candidate: T) => {
    const input = inputRef.current;
    if (!current || !input || composing.current || !items.includes(candidate)) return;
    const replacement = replaceMention(value, current, candidate);
    dismissed.current = `${replacement.text}:${replacement.caret}:${replacement.selectionEnd}`;
    lastChoice.current = { context: contextKey, name: getName(candidate) };
    setMention(null); onValueChange(replacement.text, candidate);
    requestAnimationFrame(() => { if (inputRef.current !== input) return; input.focus({ preventScroll: true }); input.setSelectionRange(replacement.caret, replacement.selectionEnd); });
  };

  return <>
    <textarea {...props} ref={inputRef} value={value} disabled={disabled} aria-autocomplete="list" aria-haspopup="listbox" aria-expanded={Boolean(current)} aria-controls={current ? `${id}-listbox` : undefined} aria-activedescendant={items[selected] ? `${id}-option-${selected}` : undefined} aria-describedby={props["aria-describedby"] || `${id}-hint`}
      onChange={event => { dismissed.current = ""; onValueChange(event.target.value); syncMention(event.currentTarget); }}
      onSelect={event => { syncMention(event.currentTarget); onSelect?.(event); }}
      onClick={event => { dismissed.current = ""; syncMention(event.currentTarget); onClick?.(event); }}
      onKeyUp={event => { if (!["Escape", "Enter", "ArrowUp", "ArrowDown"].includes(event.key)) syncMention(event.currentTarget); onKeyUp?.(event); }}
      onKeyDown={event => {
        if (current && !composing.current && !event.nativeEvent.isComposing && event.keyCode !== 229) {
          if ((event.key === "ArrowDown" || event.key === "ArrowUp") && items.length) { event.preventDefault(); setSelected(index => (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length); }
          else if (event.key === "Enter" && items[selected]) { event.preventDefault(); choose(items[selected]); }
        }
        onKeyDown?.(event);
      }}
      onBlur={event => { close(); onBlur?.(event); }}
      onCompositionStart={event => { composing.current = true; setMention(null); onCompositionStart?.(event); }}
      onCompositionEnd={event => { composing.current = false; dismissed.current = ""; syncMention(event.currentTarget); onCompositionEnd?.(event); }} />
    <p id={`${id}-hint`} className="confirmation-mention-hint">{hint}</p>
    {current ? createPortal(<div ref={panelRef} className="confirmation-entry-options confirmation-mention-popup" style={position} onMouseDown={event => event.preventDefault()}>
      <div className="confirmation-mention-heading">{popupTitle}<span>↑↓ 选择 · Enter 确认 · Esc 收起</span></div>
      <div id={`${id}-listbox`} role="listbox" aria-label={listLabel} aria-busy={!ready}>
        {items.map((candidate, index) => {
          const presentation = renderCandidate?.(candidate), label = getLabel(candidate);
          return <button type="button" role="option" tabIndex={-1} id={`${id}-option-${index}`} key={getKey(candidate)} aria-label={label} aria-describedby={presentation?.description ? `${id}-details-${index}` : undefined} aria-selected={selected === index} onMouseMove={() => setSelected(index)} onClick={() => choose(candidate)}>
            {presentation ? <>{presentation.content}{presentation.description ? <span id={`${id}-details-${index}`}>{presentation.description}</span> : null}</> : label}
          </button>;
        })}
      </div>
      {!ready || !items.length ? <div role="status">{!ready ? `正在查询${resourceLabel}…` : result.error ? `${resourceLabel}查询失败，请修改关键词后重试` : `未找到${resourceLabel}，请调整关键词`}</div> : null}
    </div>, inputRef.current?.closest(".confirmation-sheet") || document.body) : null}
  </>;
}
